import { useEffect, useState } from "react";
import type { Entite } from "@hopitech/contracts";
import { creerEntite, mesEntites } from "../lib/api.ts";
import { useApp } from "../app/state.tsx";

/** C1 — Scan QR → fiche prospect (mobile). Le prospect est enregistré comme un lead durable. */
type Mode = "idle" | "found" | "dup" | "offline";

const TAGS = ["Devis", "Chaud", "À rappeler", "Curieux"];
const PROSPECT = { nom: "Guy Allouard", role: "Ingénieur hospitalier · Services techniques", org: "Centre hospitalier de Mayotte — Mamoudzou", email: "g.allouard@chmayotte.fr" };

export function Scan(): React.ReactElement {
  const app = useApp();
  const [mode, setMode] = useState<Mode>("idle");
  const [tags, setTags] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const [leads, setLeads] = useState<Entite[]>([]);

  async function chargerLeads(): Promise<void> {
    try { setLeads((await mesEntites("lead", app.edition.id)).entites); } catch { setLeads([]); }
  }
  useEffect(() => { void chargerLeads(); /* eslint-disable-next-line */ }, [app.edition.id]);

  async function enregistrer(): Promise<void> {
    await creerEntite("lead", app.edition.id, {
      proprietaire: app.compte,
      visiteur: PROSPECT.nom,
      organisation: PROSPECT.org,
      email: PROSPECT.email,
      note,
      tags: [...tags].join(", "),
    });
    setMode("idle"); setNote(""); setTags(new Set());
    await chargerLeads();
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 400 }}>
      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--line)" }}>
          <span className="display" style={{ fontSize: 15 }}>Scanner un badge</span>
          <span className="pill pill-accent" style={{ textTransform: "none", fontFamily: "var(--sans)", fontSize: 11.5, fontWeight: 700 }}>{leads.length} prospect(s)</span>
        </div>

        {mode === "idle" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "22px 18px", alignItems: "center", justifyContent: "center", background: "#14100D" }}>
            <div style={{ position: "relative", width: 214, height: 214, borderRadius: 18, border: "2px solid #FFFFFF3A", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div style={{ width: 118, height: 118, borderRadius: 8, border: "2px dashed #FFFFFF66" }} />
              <div style={{ position: "absolute", left: 0, right: 0, height: 2, background: "#8FD18A", top: "50%", animation: "hopiScan 1.9s ease-in-out infinite alternate" }} />
            </div>
            <span style={{ fontSize: 13, color: "#FFFFFFB8", textAlign: "center", maxWidth: "30ch" }}>Cadrez le QR du badge visiteur. Le scan fonctionne aussi hors connexion.</span>
            <button className="btn" style={{ background: "#fff", color: "#14100D" }} onClick={() => setMode("found")}>Simuler un scan</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" style={{ border: "1px solid #FFFFFF3A", background: "transparent", color: "#FFFFFFB8", fontSize: 11.5, padding: "7px 11px" }} onClick={() => setMode("dup")}>Déjà scanné</button>
              <button className="btn" style={{ border: "1px solid #FFFFFF3A", background: "transparent", color: "#FFFFFFB8", fontSize: 11.5, padding: "7px 11px" }} onClick={() => setMode("offline")}>Hors ligne</button>
            </div>
          </div>
        )}

        {mode === "found" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 11, background: "var(--ok-bg)", color: "var(--ok)", fontSize: 12.5, fontWeight: 600 }}>
              <span style={{ fontWeight: 800 }}>✓</span><span>Badge reconnu · +1 point pour le visiteur</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span className="display" style={{ fontSize: 21 }}>{PROSPECT.nom}</span>
              <span style={{ fontSize: 13.5, color: "var(--muted)" }}>{PROSPECT.role}</span>
              <span style={{ fontSize: 13.5, color: "var(--muted)" }}>{PROSPECT.org}</span>
              <span style={{ fontSize: 13, color: "var(--accent)" }}>{PROSPECT.email}</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
              <span className="eyebrow" style={{ color: "var(--faint)" }}>Intérêt</span>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {TAGS.map((t) => {
                  const on = tags.has(t);
                  return (
                    <button key={t} onClick={() => setTags((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; })}
                      style={{ border: `1px solid ${on ? "var(--accent)" : "var(--line-2)"}`, background: on ? "var(--soft)" : "var(--surface)", color: on ? "var(--accent)" : "var(--ink-2)", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t}</button>
                  );
                })}
              </div>
            </div>
            <label className="field"><span className="eyebrow" style={{ color: "var(--faint)" }}>Note libre</span>
              <textarea className="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Devis pour 12 chambres, rappeler en avril…" />
            </label>
            <div style={{ display: "flex", gap: 9, marginTop: 4 }}>
              <button className="btn btn-ghost" onClick={() => setMode("idle")}>Annuler</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => void enregistrer()}>Enregistrer le prospect</button>
            </div>
          </div>
        )}

        {mode === "dup" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 11, background: "var(--warn-bg)", color: "var(--warn)", fontSize: 12.5, fontWeight: 600 }}>
              <span style={{ fontWeight: 800 }}>i</span><span>Déjà scanné — pas de doublon créé</span>
            </div>
            <p style={{ margin: 0, fontSize: 13, color: "var(--muted)" }}>La fiche existante est mise à jour. Complétez votre note sans écraser la précédente.</p>
            <button className="btn btn-ghost" onClick={() => setMode("idle")}>Retour</button>
          </div>
        )}

        {mode === "offline" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 13px", borderRadius: 11, background: "var(--panel)", color: "var(--ink-2)", fontSize: 12.5, fontWeight: 600 }}>
              <span>⚡</span><span>Hors ligne — les scans partent dès le retour du réseau</span>
            </div>
            <button className="btn btn-ghost" onClick={() => setMode("idle")}>Continuer à scanner</button>
          </div>
        )}
      </div>

      {leads.length > 0 && (
        <div className="card" style={{ padding: 16 }}>
          <span className="eyebrow" style={{ color: "var(--faint)" }}>Mes prospects</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
            {leads.map((l) => {
              const d = l.donnees as Record<string, unknown>;
              return (
                <div key={l.id} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "9px 11px", border: "1px solid var(--line)", borderRadius: 9 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{String(d.visiteur ?? "")}</span>
                    <span className="mono" style={{ fontSize: 10.5, color: "var(--faint)" }}>{(l.creeLe ?? "").slice(11, 16)}</span>
                  </div>
                  {d.tags ? <span className="pill pill-accent" style={{ alignSelf: "flex-start", textTransform: "none", fontFamily: "var(--sans)", fontSize: 11 }}>{String(d.tags)}</span> : null}
                  {d.note ? <span style={{ fontSize: 12.5, color: "var(--muted)" }}>{String(d.note)}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
